import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";

/** Route wrapper. Redirects to /login when not authenticated. */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-400">
        Loading…
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ returnTo: location.pathname }} replace />;
  }
  return <>{children}</>;
}

/**
 * Gate a route on one or more backend permission keys. The caller passes
 * client-side; the server still enforces `requires(...)` on every endpoint,
 * so this is UX only (avoids rendering a page that would 403 anyway).
 *
 * Holding any one of `anyOf` grants access. Super-admins carry the `*`
 * wildcard, so they pass every check.
 */
export function RequirePermission({
  anyOf,
  children,
}: {
  anyOf: string[];
  children: React.ReactNode;
}) {
  const { hasAny, isLoading, isAuthenticated } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!hasAny(...anyOf)) {
    return (
      <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 p-4 text-amber-800 dark:text-amber-200">
        You don't have access to this section. Ask your org admin for one of these
        permissions:
        {anyOf.map((p) => (
          <code key={p} className="mx-1 rounded bg-amber-100 dark:bg-amber-900 px-1">
            {p}
          </code>
        ))}
      </div>
    );
  }
  return <>{children}</>;
}

/** Back-compat wrapper — full org administration. Prefer RequirePermission. */
export function RequireAdmin({ children }: { children: React.ReactNode }) {
  return <RequirePermission anyOf={["admin:full"]}>{children}</RequirePermission>;
}
